/**
 * Read the basic measurements (size_bytes, file_count, languages, ...)
 * of an asset, used by the Constantine pipeline-editor's cost panel to
 * project real per-asset costs instead of the 5MB placeholder.
 *
 * Hits `GET /asset/{key}/metrics` — a capability-agnostic backend route
 * that dispatches by asset type to a provider (constantine-target-metrics
 * for SCM repos, filesystem-zip for uploaded archives, ...). See
 * docs/asset-metrics-endpoint.md for the contract.
 *
 * Response handling:
 *   - 200 with `provider: "pending"`: cache hasn't been populated yet —
 *     hook returns the response so UI can show a "metrics pending" chip;
 *     consumers fall back to placeholder defaults for projection.
 *   - 404 ("no metrics provider for asset type"): asset type isn't
 *     covered. Hook returns `null`; consumers fall back the same way.
 *   - Other errors: surface via TanStack Query's error path with a
 *     human-readable default message.
 */

import { isAxiosError } from 'axios';

import { useAxios } from '@/hooks/useAxios';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { useQuery } from '@/utils/api';

export interface AssetMetricsResponse {
  size_bytes: number | null;
  file_count: number | null;
  function_count: number | null;
  languages: Record<string, number> | null;
  metric_version: string;
  provider: string;
}

export interface UseAssetMetricsOptions {
  /**
   * Asset key (`#repository#URL#Name` etc.). When empty/undefined, the
   * query is disabled and `data` stays `undefined` — caller treats this
   * as "no asset picked yet, render placeholder defaults".
   */
  assetKey?: string;
}

/**
 * Provider string indicating the backend route accepted the request but
 * the populator hasn't run yet for this asset. UI should surface a
 * "metrics pending" chip rather than treat as an error.
 */
export const METRICS_PROVIDER_PENDING = 'pending';

export const METRICS_PROVIDER_AUTH_REQUIRED = 'auth-required';

async function fetchAssetMetrics(
  axios: ReturnType<typeof useAxios>,
  assetKey: string
): Promise<AssetMetricsResponse | null> {
  try {
    const { data } = await axios.get<AssetMetricsResponse>('/asset/metrics', {
      params: { key: assetKey },
    });
    return data;
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === 404) {
      // "No provider for this asset type" — caller falls back to
      // placeholder defaults instead of treating this as an error.
      return null;
    }
    throw err;
  }
}

export function useAssetMetrics({ assetKey }: UseAssetMetricsOptions) {
  const axios = useAxios();

  return useQuery<AssetMetricsResponse | null>({
    queryKey: getQueryKey.assetMetrics(assetKey ?? ''),
    enabled: Boolean(assetKey),
    queryFn: () => fetchAssetMetrics(axios, assetKey ?? ''),
    staleTime: 60_000,
    gcTime: 300_000,
    defaultErrorMessage: 'Failed to load asset metrics',
  });
}

export interface AggregatedAssetMetrics {
  /**
   * Summed metrics across all asset keys (only non-null, non-pending,
   * non-auth-required responses are summed). `undefined` when nothing
   * usable came back — caller falls back to placeholder defaults.
   */
  target: TargetMetricsInput | undefined;
  /** Any response had provider="auth-required" — UI surfaces a banner. */
  authRequired: boolean;
  /** Any response had provider="pending" — UI surfaces a "metrics pending" chip. */
  pending: boolean;
}

interface TargetMetricsInput {
  size_bytes?: number;
  file_count?: number;
  function_count?: number;
  languages?: Record<string, number>;
}

/**
 * Fan-out variant of useAssetMetrics: fetches per-asset metrics for every
 * key in parallel, then sums size_bytes / file_count / function_count and
 * merges language histograms so the cost panel projects TOTAL run cost
 * (one scan per target) instead of single-asset cost.
 *
 * Disabled (returns the empty-aggregate sentinel) when assetKeys is empty.
 */
export function useAggregatedAssetMetrics(
  assetKeys: readonly string[]
): AggregatedAssetMetrics {
  const axios = useAxios();

  const query = useQuery<AggregatedAssetMetrics>({
    queryKey: getQueryKey.aggregatedAssetMetrics(assetKeys),
    enabled: assetKeys.length > 0,
    queryFn: async () => {
      const responses = await Promise.all(
        assetKeys.map(key => fetchAssetMetrics(axios, key))
      );
      return aggregateMetrics(responses);
    },
    staleTime: 60_000,
    gcTime: 300_000,
    defaultErrorMessage: 'Failed to load asset metrics',
  });

  return (
    query.data ?? { target: undefined, authRequired: false, pending: false }
  );
}

function aggregateMetrics(
  responses: (AssetMetricsResponse | null)[]
): AggregatedAssetMetrics {
  const authRequired = responses.some(
    r => r?.provider === METRICS_PROVIDER_AUTH_REQUIRED
  );
  const pending = responses.some(r => r?.provider === METRICS_PROVIDER_PENDING);

  let totalSize = 0;
  let totalFiles = 0;
  let totalFns = 0;
  const langs: Record<string, number> = {};
  let hasAny = false;

  for (const r of responses) {
    if (!r) continue;
    if (
      r.provider === METRICS_PROVIDER_PENDING ||
      r.provider === METRICS_PROVIDER_AUTH_REQUIRED
    ) {
      continue;
    }
    if (r.size_bytes !== null) {
      totalSize += r.size_bytes;
      hasAny = true;
    }
    if (r.file_count !== null) {
      totalFiles += r.file_count;
      hasAny = true;
    }
    if (r.function_count !== null) {
      totalFns += r.function_count;
      hasAny = true;
    }
    if (r.languages !== null) {
      for (const [lang, bytes] of Object.entries(r.languages)) {
        langs[lang] = (langs[lang] ?? 0) + bytes;
      }
      hasAny = true;
    }
  }

  if (!hasAny) {
    return { target: undefined, authRequired, pending };
  }

  const target: TargetMetricsInput = {};
  if (totalSize > 0) target.size_bytes = totalSize;
  if (totalFiles > 0) target.file_count = totalFiles;
  if (totalFns > 0) target.function_count = totalFns;
  if (Object.keys(langs).length > 0) target.languages = langs;

  return { target, authRequired, pending };
}

/**
 * Convert the backend response into the editor's TargetMetrics shape.
 * Returns undefined when the response is null (404 — no provider) or
 * the "pending" stub — the cost estimator's resolveTargetMetrics() will
 * then use its placeholder defaults.
 */
export function toTargetMetrics(
  response: AssetMetricsResponse | null | undefined
):
  | {
      size_bytes?: number;
      file_count?: number;
      function_count?: number;
      languages?: Record<string, number>;
    }
  | undefined {
  if (!response) return undefined;
  if (response.provider === METRICS_PROVIDER_PENDING) return undefined;
  const out: {
    size_bytes?: number;
    file_count?: number;
    function_count?: number;
    languages?: Record<string, number>;
  } = {};
  if (response.size_bytes !== null) out.size_bytes = response.size_bytes;
  if (response.file_count !== null) out.file_count = response.file_count;
  if (response.function_count !== null) {
    out.function_count = response.function_count;
  }
  if (response.languages !== null) out.languages = response.languages;
  return Object.keys(out).length > 0 ? out : undefined;
}
